import React, { useState } from 'react';
import { Container, Table, Form, Button, Alert } from 'react-bootstrap';

interface ScoreItem {
  category: string;
  question: string;
}

const scoreItems: ScoreItem[] = [
  { category: 'General Liability', question: 'Do you have adequate coverage for bodily injury, property damage, and legal defense?' },
  { category: 'Property Insurance', question: 'Are your business assets fully insured against fire, theft, and natural disasters?' },
  { category: 'Business Interruption', question: 'Will your insurance cover loss of income during an operational shutdown?' },
  { category: 'Workers’ Compensation', question: 'Do you have legally compliant workers’ comp coverage for all employees?' },
  { category: 'Commercial Auto', question: 'Are all business vehicles and drivers properly insured with adequate limits?' },
  { category: 'Cyber Liability', question: 'Do you have coverage for data breaches, cyberattacks, and digital liability?' },
  { category: 'Professional Liability (E&O)', question: 'Does your policy protect against claims of errors, omissions, or negligence?' },
  { category: 'Employment Practices Liability (EPLI)', question: 'Do you have protection from employment-related lawsuits?' },
  { category: 'Umbrella Insurance', question: 'Do you have an umbrella policy to cover high-limit claims?' },
  { category: 'Product Liability', question: 'Are you covered against product defect claims?' },
  { category: 'Directors & Officers (D&O)', question: 'Are executives protected from personal liability for business decisions?' },
  { category: 'Data Backup & Recovery', question: 'Do you have a plan and support for restoring data after loss?' },
  { category: 'Legal Compliance', question: 'Are your policies up-to-date with local and federal regulations?' },
  { category: 'Contractual Requirements', question: 'Do you meet insurance obligations from vendors, landlords, or clients?' },
  { category: 'Premium Affordability', question: 'Are your premiums manageable and budgeted?' },
  { category: 'Claims Experience', question: 'Does your provider offer fast and fair claims processing?' },
  { category: 'Agent/Broker Support', question: 'Do you have a knowledgeable agent or broker helping manage risk?' },
  { category: 'Annual Reviews', question: 'Do you review and update coverage annually?' },
  { category: 'Specialized Coverage', question: 'Do you have niche coverage applicable to your business?' },
  { category: 'Risk Management Plan', question: 'Is there a risk strategy alongside your insurance coverage?' },
];

const getRiskLevel = (score: number): string => {
  if (score >= 80) return 'Excellent – Your business is well-protected.';
  if (score >= 60) return 'Good – Review and fine-tune policies to close small gaps.';
  if (score >= 40) return 'Fair – Some important coverages are missing or insufficient.';
  return 'High Risk – Immediate review with an insurance professional is recommended.';
};

const BusinessInsuranceScoreCard: React.FC = () => {
  const [scores, setScores] = useState<number[]>(Array(scoreItems.length).fill(0));
  const [showResults, setShowResults] = useState(false);

  const handleScoreChange = (index: number, value: number) => {
    const updatedScores = [...scores];
    updatedScores[index] = value;
    setScores(updatedScores);
  };

  const totalScore = scores.reduce((a, b) => a + b, 0);
  const riskLevel = getRiskLevel(totalScore);

  return (
    <Container className="my-4">
      <h2 className="mb-4 text-center">Business Insurance Score Card</h2>
      <Table bordered responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Category</th>
            <th>Question</th>
            <th>Score (1-5)</th>
          </tr>
        </thead>
        <tbody>
          {scoreItems.map((item, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{item.category}</td>
              <td>{item.question}</td>
              <td>
                <Form.Select
                  value={scores[index]}
                  onChange={(e) => handleScoreChange(index, parseInt(e.target.value))}
                >
                  <option value={0}>Select</option>
                  {[1, 2, 3, 4, 5].map((num) => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </Form.Select>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="text-center">
        <Button onClick={() => setShowResults(true)}>Calculate Score</Button>
      </div>
      {showResults && (
        <Alert variant="info" className="mt-4 text-center">
          <h5>Total Score: {totalScore} / 100</h5>
          <p><strong>{riskLevel}</strong></p>
        </Alert>
      )}
    </Container>
  );
};

export default BusinessInsuranceScoreCard;
